import { supabase } from '@/lib/supabase'
import { rpc } from './rpc'

/**
 * Everything the TOIL screens read and write.
 *
 * Reads go through views rather than functions, unlike sickness. Sickness
 * needed functions because front_desk holds no select on any sickness table
 * and the column list inside the function was the access control. Every TOIL
 * viewer is a real member of the organisation, so row level security on the
 * tables is the boundary and the security_invoker views inherit it: a head of
 * department sees their own departments through the same policy that governs
 * the table.
 *
 * Writes all go through functions, because the rules cannot live in a screen.
 * A reason is required by a check constraint, an authorisation must be a
 * different person from the approval, and the authorised hours are frozen at
 * the moment of signing. A client that knows the table name cannot get round
 * any of that: toil_events has no write policy at all.
 */

export type ToilStatus = 'observed' | 'hod_approved' | 'authorised' | 'rejected' | 'not_eligible'
export type ToilPayBasis = 'salaried' | 'hourly' | 'unclear'
export type ToilWriteStatus = 'not_applicable' | 'pending' | 'written' | 'failed' | 'blocked'
export type ToilCreditStatus =
  | 'not_required'
  | 'pending'
  | 'written'
  | 'entered_by_person'
  | 'blocked'
  | 'failed'

export interface ToilEvent {
  id: string
  employmentId: string
  personId: string
  fullName: string
  departmentName: string | null
  shiftDate: string
  hours: number
  /** Frozen at authorisation. Null until then, and it is the only figure that counts. */
  authorisedHours: number | null
  payBasis: ToilPayBasis
  /** 'set_by_person' or 'planday_absence_account'. Only one of those is worth defending. */
  payBasisSource: string
  /**
   * In words, from the rule that decided it. Never composed by a screen:
   * "holds both kinds of account" and "holds no accounts at all" both produce
   * 'unclear' and are different problems with different fixes.
   */
  payBasisWhy: string | null
  status: ToilStatus
  hodReason: string | null
  hodApprovedAt: string | null
  hodApprovedByEmail: string | null
  authorisedAt: string | null
  authorisedByEmail: string | null
  /**
   * The approver was also the authoriser, which HR and the GM may be. Hours
   * became owed on one signature rather than two, and that is shown rather
   * than left to look like an ordinary two-person approval.
   */
  singleSignature: boolean
  rejectedReason: string | null
  shiftWriteStatus: ToilWriteStatus
  shiftWriteError: string | null
  creditStatus: ToilCreditStatus
  isLong: boolean
  hoursChangedSinceAuthorisation: boolean
}

interface BoardRow {
  id: string
  employment_id: string
  person_id: string
  full_name: string
  department_name: string | null
  shift_date: string
  hours: number | string
  authorised_hours: number | string | null
  pay_basis: ToilPayBasis
  pay_basis_source: string
  pay_basis_why: string | null
  status: ToilStatus
  hod_reason: string | null
  hod_approved_at: string | null
  hod_approved_by_email: string | null
  authorised_at: string | null
  authorised_by_email: string | null
  single_signature: boolean
  rejected_reason: string | null
  shift_write_status: ToilWriteStatus
  shift_write_error: string | null
  planday_credit_status: ToilCreditStatus
  is_long: boolean | null
  hours_changed_since_authorisation: boolean | null
}

/** Postgres numeric arrives as a string. Reading it as a number without this gives NaN. */
function num(v: number | string | null): number {
  if (v === null) return 0
  return typeof v === 'number' ? v : Number(v)
}

function toEvent(r: BoardRow): ToilEvent {
  return {
    id: r.id,
    employmentId: r.employment_id,
    personId: r.person_id,
    fullName: r.full_name,
    departmentName: r.department_name,
    shiftDate: r.shift_date,
    hours: num(r.hours),
    authorisedHours: r.authorised_hours === null ? null : num(r.authorised_hours),
    payBasis: r.pay_basis,
    payBasisSource: r.pay_basis_source,
    payBasisWhy: r.pay_basis_why,
    status: r.status,
    hodReason: r.hod_reason,
    hodApprovedAt: r.hod_approved_at,
    hodApprovedByEmail: r.hod_approved_by_email,
    authorisedAt: r.authorised_at,
    authorisedByEmail: r.authorised_by_email,
    singleSignature: r.single_signature,
    rejectedReason: r.rejected_reason,
    shiftWriteStatus: r.shift_write_status,
    shiftWriteError: r.shift_write_error,
    creditStatus: r.planday_credit_status,
    isLong: r.is_long ?? false,
    hoursChangedSinceAuthorisation: r.hours_changed_since_authorisation ?? false,
  }
}

const BOARD_COLUMNS =
  'id, employment_id, person_id, full_name, department_name, shift_date, hours, authorised_hours,' +
  ' pay_basis, pay_basis_source, pay_basis_why, status, hod_reason, hod_approved_at,' +
  ' hod_approved_by_email,' +
  ' authorised_at, authorised_by_email, single_signature, rejected_reason,' +
  ' shift_write_status, shift_write_error,' +
  ' planday_credit_status, is_long, hours_changed_since_authorisation'

export interface ToilBoard {
  awaitingHod: ToilEvent[]
  awaitingAuthorisation: ToilEvent[]
  payBasisUnclear: ToilEvent[]
  /** Hourly people. Their extension is a pay matter, and it is shown rather than hidden. */
  paidExtensions: ToilEvent[]
  authorised: ToilEvent[]
  /**
   * Authorised, and Planday has not been seen to credit it. On HOTEL1, 7 of 49
   * TOIL Worked shifts were never credited, so this is a real queue and not a
   * theoretical one.
   */
  creditNotSeen: ToilEvent[]
  /** The Planday write is outstanding or errored. */
  writeOutstanding: ToilEvent[]
  canAuthorise: boolean
  /**
   * Settling a pay basis needs app.can_write_org, which is hr, org_admin and
   * group_admin. NOT gm, who can authorise but not decide this, and not
   * manager. Kept separate from canAuthorise rather than folded into one
   * "canDoAdminThings", because the database draws the line in two different
   * places and a screen that draws one line will offer somebody a button that
   * is certain to be refused.
   */
  canSettlePayBasis: boolean
}

export async function loadToilBoard(organisationId: string): Promise<ToilBoard> {
  const [{ data, error }, access] = await Promise.all([
    supabase
      .from('toil_board')
      .select(BOARD_COLUMNS)
      .eq('organisation_id', organisationId)
      .order('shift_date', { ascending: false }),
    rpc<{ organisation_id: string; roles: string[] }[]>('my_org_access'),
  ])

  if (error !== null) throw new Error(error.message)
  const rows = ((data ?? []) as unknown as BoardRow[]).map(toEvent)

  const roles = (access ?? []).find((a) => a.organisation_id === organisationId)?.roles ?? []
  const canSettlePayBasis = roles.some((r) => r === 'hr' || r === 'org_admin' || r === 'group_admin')
  const canAuthorise = canSettlePayBasis || roles.includes('gm')

  const unclear = rows.filter((r) => r.payBasis === 'unclear' && r.status === 'observed')

  return {
    // An unclear pay basis cannot be approved, so it belongs in its own list
    // rather than sitting in the HOD's queue looking actionable.
    awaitingHod: rows.filter((r) => r.status === 'observed' && r.payBasis === 'salaried'),
    awaitingAuthorisation: rows.filter((r) => r.status === 'hod_approved'),
    payBasisUnclear: unclear,
    paidExtensions: rows.filter((r) => r.status === 'not_eligible'),
    // The lists are mutually exclusive, because a row appearing in two of them
    // renders twice and reads as a duplicate record rather than as two views
    // of the same one. The TILES may overlap, since a count is obviously a
    // count; a repeated card is not obviously a repeat.
    authorised: rows.filter(
      (r) => r.status === 'authorised' && r.creditStatus !== 'pending' && r.creditStatus !== 'blocked',
    ),
    creditNotSeen: rows.filter(
      (r) => r.status === 'authorised' && (r.creditStatus === 'pending' || r.creditStatus === 'blocked'),
    ),
    writeOutstanding: rows.filter(
      (r) => r.shiftWriteStatus === 'pending' || r.shiftWriteStatus === 'blocked' || r.shiftWriteStatus === 'failed',
    ),
    canAuthorise,
    canSettlePayBasis,
  }
}

export interface PersonToilBalance {
  employmentId: string
  /** Ours: authorised from extensions since go-live. Complete and provable. */
  authorisedHours: number
  authorisedEvents: number
  awaitingHodHours: number
  awaitingAuthorisationHours: number
  /** Planday's: cumulative since 2021, maintained by hand, and can be negative. */
  plandayBalanceHours: number | null
  plandayReadAt: string | null
  plandayAccountStatus: string | null
  /** The gap. A reconciliation figure, not an error and not a total. */
  plandayLessAuthorised: number | null
}

export async function loadPersonToil(
  organisationId: string,
  personId: string,
): Promise<{ balance: PersonToilBalance | null; events: ToilEvent[] }> {
  const [balance, events] = await Promise.all([
    supabase
      .from('toil_person_balances')
      .select(
        'employment_id, authorised_hours, authorised_events, awaiting_hod_hours,' +
          ' awaiting_authorisation_hours, planday_balance_hours, planday_read_at,' +
          ' planday_account_status, planday_less_authorised',
      )
      .eq('organisation_id', organisationId)
      .eq('person_id', personId)
      .maybeSingle(),
    supabase
      .from('toil_board')
      .select(BOARD_COLUMNS)
      .eq('organisation_id', organisationId)
      .eq('person_id', personId)
      .order('shift_date', { ascending: false }),
  ])

  if (balance.error !== null) throw new Error(balance.error.message)
  if (events.error !== null) throw new Error(events.error.message)

  const b = balance.data as {
    employment_id: string
    authorised_hours: number | string
    authorised_events: number
    awaiting_hod_hours: number | string
    awaiting_authorisation_hours: number | string
    planday_balance_hours: number | string | null
    planday_read_at: string | null
    planday_account_status: string | null
    planday_less_authorised: number | string | null
  } | null

  return {
    balance:
      b === null
        ? null
        : {
            employmentId: b.employment_id,
            authorisedHours: num(b.authorised_hours),
            authorisedEvents: b.authorised_events,
            awaitingHodHours: num(b.awaiting_hod_hours),
            awaitingAuthorisationHours: num(b.awaiting_authorisation_hours),
            plandayBalanceHours: b.planday_balance_hours === null ? null : num(b.planday_balance_hours),
            plandayReadAt: b.planday_read_at,
            plandayAccountStatus: b.planday_account_status,
            plandayLessAuthorised:
              b.planday_less_authorised === null ? null : num(b.planday_less_authorised),
          },
    events: ((events.data ?? []) as unknown as BoardRow[]).map(toEvent),
  }
}

export async function approveToil(eventId: string, reason: string): Promise<void> {
  await rpc('approve_toil', { p_event: eventId, p_reason: reason })
}

export async function authoriseToil(eventId: string): Promise<void> {
  await rpc('authorise_toil', { p_event: eventId })
}

export async function rejectToil(eventId: string, reason: string): Promise<void> {
  await rpc('reject_toil', { p_event: eventId, p_reason: reason })
}

export async function unauthoriseToil(eventId: string, reason: string): Promise<void> {
  await rpc('unauthorise_toil', { p_event: eventId, p_reason: reason })
}

export async function setToilPayBasis(
  organisationId: string,
  employmentId: string,
  basis: 'salaried' | 'hourly',
  reason?: string,
): Promise<number> {
  return await rpc<number>('set_toil_pay_basis', {
    p_organisation: organisationId,
    p_employment: employmentId,
    p_basis: basis,
    p_reason: reason ?? null,
  })
}

/** Hours, to one decimal, with the unit. Planday reports TOIL in hours and only hours. */
export function hours(n: number | null): string {
  if (n === null) return 'not read'
  return `${n.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1')}h`
}
